import { h } from 'preact';
import { useCallback, useMemo, useRef, useState } from 'preact/hooks';
import { usePlayerContext, useStyles } from '../../hooks';
import { ZAP_PROGRAM_DEFAULT_IMAGE_SVG, ZAP_PROGRAM_GRADIENT_BACKDROP, ZAP_PROGRAM_MAIN_TITLE, ZAP_PROGRAM_PREVIEW, ZAP_PROGRAM_SECOND_TITLE, ZAP_PROGRAM_SQUARE, ZAP_PROGRAM_SQUARE_BACKDROP, ZAP_PROGRAM_TITLES, ZAP_PROGRAM_WRAPPER } from './styles';
import { connect, withBreakPoints } from '../../hoc';
import { USER_CLICK, USER_KEYBOARD } from '../../../../types';
import { PROGRAM_CLICKED_EVENTS, ZAPPING_NAME } from '../wrappers/Zap/constants';
import { ACTIVE_ITEM_BORDER_COLOR, HOVER_ITEM_BACKGROUND, FTV_OUTLINE_COLOR } from '../../theme/colors';
import { loadProgram } from '../../utils/list';
import { isValidateKeyboard } from '../../utils/keyboard';

function Program({
  metadata,
  active,
  activeProgramMetadata = {},
  currentTab,
  programNumber,
  channels,
  title,
  preTitle,
  additionalTitle
}) {
  const player = usePlayerContext();
  const setStyles = useStyles();
  const refContainer = useRef(null);
  const [isHover, setIsHover] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const isSquarePicturesStyle = currentTab === ZAPPING_NAME;

  const PROGRAM_VISUAL_STYLE = isSquarePicturesStyle ? {
    ...ZAP_PROGRAM_SQUARE_BACKDROP,
    backgroundImage: `url('${metadata.logoFull}')`
  } : {
    ...ZAP_PROGRAM_GRADIENT_BACKDROP,
    backgroundImage: metadata?.image ? `url("${metadata.image}")` : `url("${ZAP_PROGRAM_DEFAULT_IMAGE_SVG}")`
  };

  const handleClick = useCallback((fromKeyboard) => {
    const source = fromKeyboard ? USER_KEYBOARD : USER_CLICK;

    player.userEvents$.next({
      action: PROGRAM_CLICKED_EVENTS[currentTab],
      value: {
        id: metadata.id,
        previousChannel: activeProgramMetadata.name,
        currentChannel: metadata.name,
        ...metadata
      },
      source
    });

    loadProgram({
      metadata, source, channels, list: channels, programNumber,
      currentTab, player, title, preTitle, additionalTitle
    });
  }, [metadata.id, activeProgramMetadata?.name]);

  const style = useMemo(() => ({
    ...setStyles(ZAP_PROGRAM_WRAPPER),
    ...active && !isFocused && ({ boxShadow: `${ACTIVE_ITEM_BORDER_COLOR} 0 0 0 3px inset` }),
    ...isFocused && ({ boxShadow: `${FTV_OUTLINE_COLOR} 0 0 0 3px inset` }),
    ...isHover && ({ background: HOVER_ITEM_BACKGROUND }),
    ...isPressed && ({ background: ACTIVE_ITEM_BORDER_COLOR })
  }), [active, isFocused, isHover, isPressed]);

  return (
    <div
      ref={refContainer}
      style={style}
      role="radio"
      aria-checked={active ? 'true' : 'false'}
      onKeyUp={(e) => {
        if (isValidateKeyboard(e)) {
          handleClick(true);
          setIsPressed(false);
          setTimeout(() => refContainer.current?.focus(), 1000);
        }
      }}
      onKeyDown={(e) => {
        if (isValidateKeyboard(e)) {
          // Prevent space scroll when validating current program
          e.preventDefault();
          e.stopPropagation();
          setIsPressed(true);
        }
      }}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => {
        handleClick();
        setIsPressed(false);
      }}
      tabIndex="0"
      className="ftv-magneto-pointer ftv-magneto--selectable"
    >
      <div style={setStyles(isSquarePicturesStyle ? ZAP_PROGRAM_SQUARE : ZAP_PROGRAM_PREVIEW)}>
        <div style={PROGRAM_VISUAL_STYLE} />
      </div>
      <div style={setStyles(ZAP_PROGRAM_TITLES)}>
        <div style={setStyles(ZAP_PROGRAM_MAIN_TITLE)}>{metadata.title}</div>
        <div style={setStyles(ZAP_PROGRAM_SECOND_TITLE)}>
          {metadata.preTitle ? `${metadata.preTitle} ` : ''}
          {metadata.additionalTitle}
        </div>
      </div>
    </div>
  );
}

const selector = ({
  playlist: { playlist },
  media: { title, preTitle, additionalTitle }
}) => ({
  playlist,
  title,
  preTitle,
  additionalTitle
});

export default withBreakPoints(connect(selector)(Program));
